// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { getAuthToken } from '../../utils/auth';
import './HabitTracker.css';

function toISODateLocal(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function addDaysLocal(dateStr, days) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + days);
    return toISODateLocal(dt);
}

function parseJsonSafe(text) {
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (_e) {
        return null;
    }
}

// Preset modern vibrant colors
const PRESET_COLORS = [
    '#7C75F8', // Lavender
    '#F8A155', // Peach / Amber
    '#FA6363', // Coral / Red
    '#45B7B8', // Teal / Mint
    '#E5C642', // Olive / Mustard
    '#4FACFE', // Sky Blue
    '#F857A6', // Pink / Rose
    '#9B51E0', // Violet
];

// Preset icons matching sample image (including all 11 icons from screenshot + general)
const PRESET_ICONS = [
    { icon: 'fi-rr-book-alt', label: 'Reading' },
    { icon: 'fi-rr-edit-alt', label: 'Journal' },
    { icon: 'fi-rr-dumbbell', label: 'Workout' },
    { icon: 'fi-rr-gym', label: 'Stretch' },
    { icon: 'fi-rr-spa', label: 'Meditate' },
    { icon: 'fi-rr-ban', label: 'No Social' },
    { icon: 'fi-rr-music', label: 'Music / Guitar' },
    { icon: 'fi-rr-walking', label: 'Walking' },
    { icon: 'fi-rr-drink-alt', label: 'Water' },
    { icon: 'fi-rr-bed', label: 'Sleep' },
    { icon: 'fi-rr-sparkles', label: 'Sparkles' },
];

// Complete Emoji Library categorized for the subpopup
const EMOJI_CATEGORIES = [
    {
        name: 'Productivity & Study',
        emojis: ['📖', '📚', '✍️', '📝', '💻', '💡', '🧠', '🎯', '🚀', '⏱️', '⏳', '📌', '📈', '📊', '🎓', '💼', '🔬', '🎨']
    },
    {
        name: 'Fitness & Health',
        emojis: ['🏃‍♂️', '🏃‍♀️', '🏋️', '🧘‍♀️', '🤸', '🚴‍♂️', '🏊‍♂️', '🧗', '⛹️', '⚽', '🥊', '💧', '🥗', '🍎', '🥑', '🥦', '🛌', '💤', '💊', '🩺']
    },
    {
        name: 'Mindfulness & Lifestyle',
        emojis: ['🌿', '🌱', '☀️', '🌙', '⭐', '✨', '🔥', '🕯️', '☕', '🍵', '🚿', '🧹', '🪴', '🚶‍♂️', '🧘', '❤️', '🕊️', '🎶', '🎸', '🎹']
    },
    {
        name: 'Discipline & Habits',
        emojis: ['🚫', '📵', '🛑', '🛡️', '⏰', '🔒', '💰', '💵', '💳', '📦', '🛒', '🍽️', '🚭', '📵', '⚖️', '🏅', '🏆', '💎']
    },
    {
        name: 'Smilies & Mood',
        emojis: ['😀', '😎', '🤩', '🥳', '💪', '🙌', '👏', '🤝', '🎉', '🌟', '🌈', '⚡', '💯', '🌸', '🌻', '🐶', '🐱']
    }
];

// Goal / Frequency Options
const GOAL_OPTIONS = [
    'Daily',
    'Daily • 0/1',
    'Daily • 1/1',
    'No Goal',
    'No Goal • 0/1',
    'No Goal • 1/1',
    'Weekly • 1/3',
    'Weekly • 2/3',
    'Weekly • 3/3',
    'Weekly • 3/5',
    'Weekly • 5/5',
    'Custom'
];

const DEFAULT_GUEST_HABITS = [
    { _id: 'sample-1', name: 'Read 20 pages', color: '#7C75F8', icon: 'fi-rr-book-alt', goal: 'Daily • 0/1', starred: true, active: true },
    { _id: 'sample-2', name: 'Journal', color: '#9B51E0', icon: 'fi-rr-edit-alt', goal: 'No Goal • 1/1', starred: false, active: true },
    { _id: 'sample-3', name: 'Stretch', color: '#F8A155', icon: 'fi-rr-gym', goal: 'No Goal • 0/1', starred: false, active: true },
    { _id: 'sample-4', name: 'Workout', color: '#FA6363', icon: 'fi-rr-dumbbell', goal: 'Weekly • 2/3', starred: false, active: true },
    { _id: 'sample-5', name: 'Meditate', color: '#45B7B8', icon: 'fi-rr-spa', goal: 'No Goal • 1/1', starred: false, active: true },
    { _id: 'sample-6', name: 'No social media', color: '#E5C642', icon: 'fi-rr-ban', goal: 'No Goal • 0/1', starred: false, active: true },
    { _id: 'sample-7', name: 'Practice guitar', color: '#F857A6', icon: 'fi-rr-music', goal: 'Weekly • 3/2', starred: false, active: true },
    { _id: 'sample-8', name: 'Morning walk', color: '#4FACFE', icon: 'fi-rr-walking', goal: 'No Goal • 1/1', starred: false, active: true },
    { _id: 'sample-9', name: 'Drink water', color: '#00C9FF', icon: 'fi-rr-drink-alt', goal: 'No Goal • 0/8', starred: false, active: true },
    { _id: 'sample-10', name: 'Sleep by 11', color: '#A18CD1', icon: 'fi-rr-bed', goal: 'No Goal • 0/1', starred: false, active: true },
];

function isEmoji(str) {
    if (!str) return false;
    if (str.startsWith('fi-') || str.startsWith('fi ')) return false;
    return true;
}

function renderHabitIcon(iconStr) {
    if (!iconStr) {
        return <i className="fi fi-rr-sparkles"></i>;
    }
    if (isEmoji(iconStr)) {
        return <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{iconStr}</span>;
    }
    const cleanClass = iconStr.startsWith('fi ') ? iconStr : `fi ${iconStr}`;
    return <i className={cleanClass}></i>;
}

function HabitTracker() {
    const navigate = useNavigate();
    const token = useMemo(() => getAuthToken(), []);

    const [habits, setHabits] = useState([]);
    const [completionMap, setCompletionMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [cellInFlight, setCellInFlight] = useState({});

    // Detail Modal State (Screenshots 3 & 4)
    const [activeDetailHabit, setActiveDetailHabit] = useState(null);
    const [detailYearView, setDetailYearView] = useState(false);
    const [calendarMonthDate, setCalendarMonthDate] = useState(new Date());

    // Create / Edit Modal State
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingHabit, setEditingHabit] = useState(null);
    const [formName, setFormName] = useState('');
    const [formColor, setFormColor] = useState('#7C75F8');
    const [formIcon, setFormIcon] = useState('fi-rr-sparkles');
    const [formGoal, setFormGoal] = useState('Daily');
    const [formStarred, setFormStarred] = useState(false);
    const [customGoal, setCustomGoal] = useState('');

    // Subpopup Emoji Library Modal State
    const [showEmojiModal, setShowEmojiModal] = useState(false);
    const [emojiSearch, setEmojiSearch] = useState('');
    const [selectedEmojiCategory, setSelectedEmojiCategory] = useState('All');

    // Habit Stats map
    const [statsByHabit, setStatsByHabit] = useState({});

    const controllersRef = useRef([]);
    const today = useMemo(() => toISODateLocal(new Date()), []);

    // 7-day strip (Current week days Mon-Sun)
    const currentWeekDays = useMemo(() => {
        const now = new Date();
        const day = now.getDay();
        const diff = day === 0 ? -6 : 1 - day; // Monday start
        const monday = new Date(now);
        monday.setDate(now.getDate() + diff);

        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            return {
                iso: toISODateLocal(d),
                label: ['M', 'T', 'W', 'T', 'F', 'S', 'S'][i],
                dayNum: d.getDate(),
                isToday: toISODateLocal(d) === today,
            };
        });
    }, [today]);

    const request = useCallback(async (url, options = {}, redirectOn401 = true) => {
        const controller = new AbortController();
        controllersRef.current.push(controller);

        const headers = {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers,
                signal: controller.signal,
            });

            if (response.status === 401 || response.status === 403) {
                if (redirectOn401) {
                    navigate('/signin', { state: { from: { pathname: '/app-store/habit-tracker' } } });
                }
                throw new Error('Unauthorized');
            }

            const text = await response.text();
            const json = parseJsonSafe(text);

            if (!response.ok) {
                const msg = json?.error?.message || json?.message || 'Request failed';
                throw new Error(msg);
            }

            return json;
        } finally {
            controllersRef.current = controllersRef.current.filter((ctrl) => ctrl !== controller);
        }
    }, [navigate, token]);

    // Check completion
    const isCompleted = useCallback((habitId, dateStr) => {
        return Boolean(completionMap?.[habitId]?.[dateStr]);
    }, [completionMap]);

    // Load data online
    const loadCore = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        setErrorMessage('');

        try {
            const from = addDaysLocal(today, -60);
            const to = addDaysLocal(today, 30);

            const [habitsRes, completionsRes] = await Promise.all([
                request(API_ENDPOINTS.HABIT_TRACKER.HABITS),
                request(`${API_ENDPOINTS.HABIT_TRACKER.COMPLETIONS}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
            ]);

            let fetchedHabits = habitsRes?.data || [];
            if (fetchedHabits.length === 0) {
                // If brand new user, seed initial starter habits
                for (const seed of DEFAULT_GUEST_HABITS.slice(0, 4)) {
                    try {
                        const seeded = await request(API_ENDPOINTS.HABIT_TRACKER.HABITS, {
                            method: 'POST',
                            body: JSON.stringify({
                                name: seed.name,
                                color: seed.color,
                                icon: seed.icon,
                                goal: seed.goal,
                                starred: seed.starred,
                            }),
                        });
                        if (seeded?.data) fetchedHabits.push(seeded.data);
                    } catch (_e) {}
                }
            }

            setHabits(fetchedHabits);
            setCompletionMap(completionsRes?.data || {});
        } catch (err) {
            if (err && err.name === 'AbortError') return;
            setErrorMessage(err.message || 'Unable to load habit tracker data');
        } finally {
            setLoading(false);
        }
    }, [request, today, token]);

    // Guest / Authenticated Initialization
    useEffect(() => {
        if (!token) {
            // Guest mode: load starter habits or local storage
            const localHabits = parseJsonSafe(localStorage.getItem('habitsy_habits')) || DEFAULT_GUEST_HABITS;
            const localCompletions = parseJsonSafe(localStorage.getItem('habitsy_completions')) || {
                'sample-1': { [today]: true },
                'sample-2': { [today]: true },
                'sample-4': { [today]: true },
            };
            setHabits(localHabits);
            setCompletionMap(localCompletions);
            setLoading(false);
            return;
        }

        loadCore();
        return () => {
            controllersRef.current.forEach((ctrl) => ctrl.abort());
            controllersRef.current = [];
        };
    }, [token, loadCore, today]);

    // Auto-dismiss messages
    useEffect(() => {
        if (!successMessage) return;
        const timer = setTimeout(() => setSuccessMessage(''), 2500);
        return () => clearTimeout(timer);
    }, [successMessage]);

    // Toggle Completion Online / Local
    const toggleCompletion = async (habitId, dateStr = today) => {
        const cellKey = `${habitId}:${dateStr}`;
        if (cellInFlight[cellKey]) return;

        const prevCompleted = isCompleted(habitId, dateStr);

        setCellInFlight((prev) => ({ ...prev, [cellKey]: true }));

        // Optimistic UI update
        setCompletionMap((prev) => {
            const habitMap = { ...(prev?.[habitId] || {}) };
            if (prevCompleted) {
                delete habitMap[dateStr];
            } else {
                habitMap[dateStr] = true;
            }
            const next = { ...prev, [habitId]: habitMap };
            if (!token) {
                localStorage.setItem('habitsy_completions', JSON.stringify(next));
            }
            return next;
        });

        if (!token) {
            setCellInFlight((prev) => {
                const next = { ...prev };
                delete next[cellKey];
                return next;
            });
            return;
        }

        try {
            const res = await request(API_ENDPOINTS.HABIT_TRACKER.TOGGLE_COMPLETION, {
                method: 'POST',
                body: JSON.stringify({ habitId, date: dateStr, today }),
            });

            const completed = !!res?.data?.completed;
            setCompletionMap((prev) => {
                const habitMap = { ...(prev?.[habitId] || {}) };
                if (completed) {
                    habitMap[dateStr] = true;
                } else {
                    delete habitMap[dateStr];
                }
                return { ...prev, [habitId]: habitMap };
            });

            if (res?.data?.stats) {
                setStatsByHabit((prev) => ({ ...prev, [habitId]: res.data.stats }));
            }
        } catch (err) {
            // Revert optimistic on error
            setCompletionMap((prev) => {
                const habitMap = { ...(prev?.[habitId] || {}) };
                if (prevCompleted) {
                    habitMap[dateStr] = true;
                } else {
                    delete habitMap[dateStr];
                }
                return { ...prev, [habitId]: habitMap };
            });
            setErrorMessage(err.message || 'Failed to toggle completion');
        } finally {
            setCellInFlight((prev) => {
                const next = { ...prev };
                delete next[cellKey];
                return next;
            });
        }
    };

    // Open Habit Form
    const openCreateHabit = () => {
        setEditingHabit(null);
        setFormName('');
        setFormColor(PRESET_COLORS[0]);
        setFormIcon('fi-rr-sparkles');
        setFormGoal('Daily');
        setCustomGoal('');
        setFormStarred(false);
        setShowFormModal(true);
    };

    const openEditHabit = (habit) => {
        setEditingHabit(habit);
        setFormName(habit.name || '');
        setFormColor(habit.color || PRESET_COLORS[0]);
        setFormIcon(habit.icon || 'fi-rr-sparkles');
        
        const isStandardGoal = GOAL_OPTIONS.includes(habit.goal);
        if (isStandardGoal) {
            setFormGoal(habit.goal);
            setCustomGoal('');
        } else {
            setFormGoal('Custom');
            setCustomGoal(habit.goal || '');
        }

        setFormStarred(Boolean(habit.starred));
        setShowFormModal(true);
    };

    // Save Habit
    const saveHabit = async (e) => {
        if (e) e.preventDefault();
        const trimmedName = formName.trim();
        if (!trimmedName) return;

        const finalGoal = formGoal === 'Custom' ? (customGoal.trim() || 'Daily') : formGoal;
        const finalIcon = formIcon || 'fi-rr-sparkles';

        const payload = {
            name: trimmedName,
            color: formColor,
            icon: finalIcon,
            goal: finalGoal,
            starred: formStarred,
            active: true,
        };

        if (!token) {
            // Guest local save
            let updatedList = [];
            if (editingHabit) {
                updatedList = habits.map((h) => (h._id === editingHabit._id ? { ...h, ...payload } : h));
                setSuccessMessage('Habit updated');
            } else {
                const newLocalHabit = { ...payload, _id: `local-${Date.now()}` };
                updatedList = [newLocalHabit, ...habits];
                setSuccessMessage('Habit created');
            }
            setHabits(updatedList);
            localStorage.setItem('habitsy_habits', JSON.stringify(updatedList));
            setShowFormModal(false);
            if (activeDetailHabit && editingHabit && activeDetailHabit._id === editingHabit._id) {
                setActiveDetailHabit((prev) => ({ ...prev, ...payload }));
            }
            return;
        }

        try {
            if (editingHabit) {
                const res = await request(API_ENDPOINTS.HABIT_TRACKER.HABIT(editingHabit._id), {
                    method: 'PUT',
                    body: JSON.stringify(payload),
                });
                if (res?.data) {
                    setHabits((prev) => prev.map((h) => (h._id === editingHabit._id ? { ...h, ...res.data } : h)));
                    if (activeDetailHabit && activeDetailHabit._id === editingHabit._id) {
                        setActiveDetailHabit((prev) => ({ ...prev, ...res.data }));
                    }
                    setSuccessMessage('Habit updated');
                }
            } else {
                const res = await request(API_ENDPOINTS.HABIT_TRACKER.HABITS, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
                if (res?.data) {
                    setHabits((prev) => [res.data, ...prev]);
                    setSuccessMessage('Habit created');
                }
            }
            setShowFormModal(false);
        } catch (err) {
            setErrorMessage(err.message || 'Failed to save habit');
        }
    };

    // Toggle Starred
    const toggleStarred = async (habit, e) => {
        if (e) e.stopPropagation();
        const nextStarred = !habit.starred;
        if (!token) {
            const nextList = habits.map((h) => (h._id === habit._id ? { ...h, starred: nextStarred } : h));
            setHabits(nextList);
            localStorage.setItem('habitsy_habits', JSON.stringify(nextList));
            if (activeDetailHabit && activeDetailHabit._id === habit._id) {
                setActiveDetailHabit((prev) => ({ ...prev, starred: nextStarred }));
            }
            return;
        }

        try {
            await request(API_ENDPOINTS.HABIT_TRACKER.HABIT(habit._id), {
                method: 'PUT',
                body: JSON.stringify({ starred: nextStarred }),
            });
            setHabits((prev) => prev.map((h) => (h._id === habit._id ? { ...h, starred: nextStarred } : h)));
            if (activeDetailHabit && activeDetailHabit._id === habit._id) {
                setActiveDetailHabit((prev) => ({ ...prev, starred: nextStarred }));
            }
        } catch (_err) {}
    };

    // Delete Habit
    const deleteHabit = async (habitId) => {
        if (!window.confirm('Delete this habit and all its history?')) return;

        if (!token) {
            const nextList = habits.filter((h) => h._id !== habitId);
            setHabits(nextList);
            localStorage.setItem('habitsy_habits', JSON.stringify(nextList));
            setActiveDetailHabit(null);
            setSuccessMessage('Habit deleted');
            return;
        }

        try {
            await request(API_ENDPOINTS.HABIT_TRACKER.HABIT(habitId), { method: 'DELETE' });
            setHabits((prev) => prev.filter((h) => String(h._id) !== String(habitId)));
            setActiveDetailHabit(null);
            setSuccessMessage('Habit deleted');
        } catch (err) {
            setErrorMessage(err.message || 'Failed to delete habit');
        }
    };

    // Starred Habit (Hero top spotlight)
    const starredHabit = useMemo(() => {
        return habits.find((h) => h.starred && h.active !== false) || habits[0] || null;
    }, [habits]);

    // Grid Habits (Others or all)
    const gridHabits = useMemo(() => {
        if (!starredHabit) return habits.filter((h) => h.active !== false);
        return habits.filter((h) => h.active !== false && String(h._id) !== String(starredHabit._id));
    }, [habits, starredHabit]);

    // Filtered Emoji Library for the Sub-popup
    const filteredEmojiCategories = useMemo(() => {
        const query = emojiSearch.trim().toLowerCase();
        return EMOJI_CATEGORIES.map((cat) => {
            if (selectedEmojiCategory !== 'All' && cat.name !== selectedEmojiCategory) {
                return null;
            }
            if (!query) return cat;
            const matched = cat.emojis.filter((e) => e.includes(query));
            return matched.length > 0 ? { ...cat, emojis: matched } : null;
        }).filter(Boolean);
    }, [emojiSearch, selectedEmojiCategory]);

    // Generate Calendar Days for active habit in detail modal (Screenshot 3)
    const calendarMonthDays = useMemo(() => {
        const year = calendarMonthDate.getFullYear();
        const month = calendarMonthDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        let startDayIdx = firstDay.getDay() - 1;
        if (startDayIdx < 0) startDayIdx = 6;

        const days = [];
        for (let i = startDayIdx; i > 0; i--) {
            const d = new Date(year, month, 1 - i);
            days.push({
                iso: toISODateLocal(d),
                num: d.getDate(),
                isOutside: true,
            });
        }

        for (let i = 1; i <= lastDay.getDate(); i++) {
            const d = new Date(year, month, i);
            const iso = toISODateLocal(d);
            days.push({
                iso,
                num: i,
                isOutside: false,
                isToday: iso === today,
            });
        }

        return days;
    }, [calendarMonthDate, today]);

    return (
        <>
            <Helmet>
                <title>Habitsy - Habit Tracker | Hari Vishnu</title>
                <meta name="description" content="Build better habits with streak tracking, 7-day progress, and annual consistency view." />
                <meta name="robots" content="index,follow" />
                <link rel="canonical" href="https://apps.harivishnu.in/habit-tracker" />
            </Helmet>

            <div className="habit-page">
                {!token && (
                    <div className="habitsy-guest-preview-banner" style={{ background: '#f0f4ff', border: '1px solid #d0e1fd', color: '#2b5282', padding: '10px 16px', borderRadius: '10px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                        <span><strong>Guest Demo:</strong> Exploring interactive mode. Your changes save locally in your browser.</span>
                        <button type="button" onClick={() => navigate('/signin', { state: { from: { pathname: '/app-store/habit-tracker' } } })} style={{ background: '#3182ce', color: '#fff', border: 'none', padding: '5px 14px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}>Sign in to sync</button>
                    </div>
                )}
                {/* Banner Notifications */}
                {(errorMessage || successMessage) && (
                    <div className={`habit-banner ${errorMessage ? 'is-error' : 'is-success'}`} role="status">
                        <i className={`fi ${errorMessage ? 'fi-rr-exclamation' : 'fi-rr-check'}`}></i>
                        <span>{errorMessage || successMessage}</span>
                    </div>
                )}

                <div className="habitsy-app-container">
                    {/* Header Row */}
                    <header className="habitsy-header">
                        <div className="habitsy-header-left">
                            <button
                                type="button"
                                className="habitsy-icon-btn"
                                aria-label="Statistics view"
                                onClick={() => starredHabit && setActiveDetailHabit(starredHabit)}
                            >
                                <i className="fi fi-rr-chart-histogram"></i>
                            </button>
                        </div>

                        <div className="habitsy-title-wrap">
                            <h1 className="habitsy-brand-title">Habitsy</h1>
                            <div className="habitsy-date-sub">
                                {new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                            </div>
                        </div>

                        <div className="habitsy-header-right">
                            <button
                                type="button"
                                className="habitsy-icon-btn"
                                onClick={openCreateHabit}
                                aria-label="Add habit"
                            >
                                <i className="fi fi-rr-plus"></i>
                            </button>
                            <button
                                type="button"
                                className="habitsy-icon-btn"
                                aria-label="Refresh"
                                onClick={() => loadCore()}
                            >
                                <i className="fi fi-rr-refresh"></i>
                            </button>
                        </div>
                    </header>

                    {/* Starred Hero Habit */}
                    {starredHabit && (
                        <section className="habitsy-starred-section">
                            <div className="habitsy-section-title">Starred</div>
                            {(() => {
                                const habitId = String(starredHabit._id);
                                const isDoneToday = isCompleted(habitId, today);
                                const color = starredHabit.color || '#7C75F8';

                                return (
                                    <div
                                        className="habitsy-starred-card"
                                        style={{ backgroundColor: color }}
                                        onClick={() => setActiveDetailHabit(starredHabit)}
                                    >
                                        <div className="habitsy-starred-top">
                                            <div className="habitsy-card-icon">
                                                {renderHabitIcon(starredHabit.icon || 'fi-rr-book-alt')}
                                            </div>
                                            <button
                                                type="button"
                                                aria-label={`Check ${starredHabit.name} for ${today}`}
                                                className={`habitsy-check-circle ${isDoneToday ? 'is-completed' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleCompletion(habitId, today);
                                                }}
                                            >
                                                {isDoneToday && <i className="fi fi-rr-check"></i>}
                                            </button>
                                        </div>

                                        <div className="habitsy-starred-info">
                                            <h2 className="habitsy-starred-title">{starredHabit.name}</h2>
                                            <div className="habitsy-starred-meta">
                                                <span>{starredHabit.goal || 'Daily'}</span>
                                                <span className="habitsy-streak-badge">
                                                    🔥 {statsByHabit[habitId]?.currentStreak || (isDoneToday ? 1 : 0)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* 7-Day Pill Row */}
                                        <div className="habitsy-week-row" onClick={(e) => e.stopPropagation()}>
                                            {currentWeekDays.map((dayObj) => {
                                                const dayDone = isCompleted(habitId, dayObj.iso);
                                                return (
                                                    <div
                                                        key={dayObj.iso}
                                                        className={`habitsy-day-pill ${dayDone ? 'is-done' : ''} ${dayObj.isToday ? 'is-today' : ''}`}
                                                        onClick={() => toggleCompletion(habitId, dayObj.iso)}
                                                        title={`${dayObj.iso}: ${dayDone ? 'Completed' : 'Pending'}`}
                                                    >
                                                        <span>{dayObj.label}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                        </section>
                    )}

                    {/* Habits Responsive Grid */}
                    <section className="habitsy-grid-section">
                        <div className="habitsy-section-title">Habits</div>

                        {gridHabits.length === 0 && !starredHabit ? (
                            <div className="habitsy-empty">
                                <i className="fi fi-rr-magic-wand"></i>
                                <p>No habits yet. Start building consistency today!</p>
                                <button type="button" className="habitsy-btn-create" onClick={openCreateHabit}>
                                    Create First Habit
                                </button>
                            </div>
                        ) : (
                            <div className="habitsy-grid">
                                {gridHabits.map((habit) => {
                                    const habitId = String(habit._id);
                                    const isDoneToday = isCompleted(habitId, today);
                                    const color = habit.color || '#F8A155';

                                    return (
                                        <article
                                            key={habitId}
                                            className={`habitsy-card ${isDoneToday ? 'is-dimmed' : ''}`}
                                            style={!isDoneToday ? { backgroundColor: color, color: '#ffffff' } : {}}
                                            onClick={() => setActiveDetailHabit(habit)}
                                        >
                                            <div className="habitsy-card-top">
                                                <div className="habitsy-card-icon">
                                                    {renderHabitIcon(habit.icon || 'fi-rr-sparkles')}
                                                </div>

                                                <button
                                                    type="button"
                                                    aria-label={`Check ${habit.name} for ${today}`}
                                                    className={`habitsy-check-circle ${isDoneToday ? 'is-completed' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleCompletion(habitId, today);
                                                    }}
                                                >
                                                    {isDoneToday && <i className="fi fi-rr-check"></i>}
                                                </button>
                                            </div>

                                            <div className="habitsy-card-content">
                                                <h3 className="habitsy-card-title">{habit.name}</h3>
                                                <div className="habitsy-card-subtitle">
                                                    <span>{habit.goal || 'Daily'}</span>
                                                    {statsByHabit[habitId]?.currentStreak > 0 && (
                                                        <span>• 🔥 {statsByHabit[habitId].currentStreak}</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* 7-day dot progress trail at bottom */}
                                            <div className="habitsy-card-dots">
                                                {currentWeekDays.map((d) => {
                                                    const dotDone = isCompleted(habitId, d.iso);
                                                    return (
                                                        <span
                                                            key={d.iso}
                                                            className={`habitsy-dot ${dotDone ? 'is-completed' : ''}`}
                                                            title={d.iso}
                                                        ></span>
                                                    );
                                                })}
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                </div>

                {/* Detail & Consistency Calendar Modal */}
                {activeDetailHabit && (
                    <div className="habitsy-modal-backdrop" onClick={() => setActiveDetailHabit(null)}>
                        <div className="habitsy-detail-modal" onClick={(e) => e.stopPropagation()}>
                            {/* Modal Top Nav */}
                            <div className="habitsy-detail-nav">
                                <button
                                    type="button"
                                    className="habitsy-nav-circle-btn"
                                    onClick={() => setActiveDetailHabit(null)}
                                    aria-label="Close"
                                >
                                    <i className="fi fi-rr-cross-small"></i>
                                </button>

                                <div className="habitsy-card-icon" style={{ background: activeDetailHabit.color || '#7C75F8', color: '#fff' }}>
                                    {renderHabitIcon(activeDetailHabit.icon || 'fi-rr-sparkles')}
                                </div>

                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        type="button"
                                        className={`habitsy-nav-circle-btn is-star ${activeDetailHabit.starred ? 'is-starred' : ''}`}
                                        onClick={(e) => toggleStarred(activeDetailHabit, e)}
                                        aria-label="Toggle Star"
                                    >
                                        <i className="fi fi-rr-star"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="habitsy-nav-circle-btn"
                                        onClick={() => openEditHabit(activeDetailHabit)}
                                        aria-label="Edit Habit"
                                    >
                                        <i className="fi fi-rr-pencil"></i>
                                    </button>
                                </div>
                            </div>

                            {/* Scrollable Center Content */}
                            <div className="habitsy-modal-scrollable-body">
                                {/* Habit Info */}
                                <div className="habitsy-detail-header">
                                    <h2 className="habitsy-detail-title">{activeDetailHabit.name}</h2>
                                    <div className="habitsy-detail-meta-tag">
                                        <span>{activeDetailHabit.goal || 'Daily'}</span>
                                        <span>•</span>
                                        <span>Current Streak: {statsByHabit[activeDetailHabit._id]?.currentStreak || 0} days</span>
                                    </div>
                                </div>

                                {/* Micro Heatmap Row */}
                                <div className="habitsy-micro-heatmap">
                                    <div className="habitsy-micro-matrix">
                                        {Array.from({ length: 7 * 16 }).map((_, idx) => {
                                            const dateObj = new Date();
                                            dateObj.setDate(dateObj.getDate() - (7 * 16 - idx));
                                            const iso = toISODateLocal(dateObj);
                                            const done = isCompleted(activeDetailHabit._id, iso);
                                            return (
                                                <div
                                                    key={iso}
                                                    className={`habitsy-micro-cell ${done ? 'is-completed' : ''}`}
                                                    style={done ? { backgroundColor: activeDetailHabit.color || '#7C75F8' } : {}}
                                                    title={`${iso}: ${done ? 'Completed' : 'Not done'}`}
                                                ></div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Action Buttons: View Switcher (Month vs Year) */}
                                <div className="habitsy-detail-actions-row">
                                    <button
                                        type="button"
                                        className="habitsy-action-chip"
                                        onClick={() => setDetailYearView(false)}
                                        style={!detailYearView ? { borderColor: activeDetailHabit.color || '#7C75F8', color: activeDetailHabit.color || '#7C75F8' } : {}}
                                    >
                                        <i className="fi fi-rr-calendar"></i>
                                        <span>Month View</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="habitsy-action-chip"
                                        onClick={() => setDetailYearView(true)}
                                        style={detailYearView ? { borderColor: activeDetailHabit.color || '#7C75F8', color: activeDetailHabit.color || '#7C75F8' } : {}}
                                    >
                                        <i className="fi fi-rr-apps"></i>
                                        <span>Year View</span>
                                    </button>
                                </div>

                                {/* View 1: Month Calendar View */}
                                {!detailYearView && (
                                    <div className="habitsy-month-view">
                                        <div className="habitsy-month-nav">
                                            <button
                                                type="button"
                                                className="habitsy-arrow-btn"
                                                onClick={() => {
                                                    const d = new Date(calendarMonthDate);
                                                    d.setMonth(d.getMonth() - 1);
                                                    setCalendarMonthDate(d);
                                                }}
                                            >
                                                <i className="fi fi-rr-angle-left"></i>
                                            </button>
                                            <h3 className="habitsy-month-title">
                                                {calendarMonthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                                            </h3>
                                            <button
                                                type="button"
                                                className="habitsy-arrow-btn"
                                                onClick={() => {
                                                    const d = new Date(calendarMonthDate);
                                                    d.setMonth(d.getMonth() + 1);
                                                    setCalendarMonthDate(d);
                                                }}
                                            >
                                                <i className="fi fi-rr-angle-right"></i>
                                            </button>
                                        </div>

                                        <div className="habitsy-cal-weekdays">
                                            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((w) => (
                                                <span key={w}>{w}</span>
                                            ))}
                                        </div>

                                        <div className="habitsy-cal-grid">
                                            {calendarMonthDays.map((cell) => {
                                                const done = isCompleted(activeDetailHabit._id, cell.iso);
                                                return (
                                                    <button
                                                        key={cell.iso}
                                                        type="button"
                                                        className={`habitsy-cal-cell ${cell.isOutside ? 'is-outside' : ''} ${done ? 'is-completed' : ''} ${cell.isToday ? 'is-today' : ''}`}
                                                        style={done ? { backgroundColor: activeDetailHabit.color || '#7C75F8', color: '#fff' } : {}}
                                                        onClick={() => toggleCompletion(activeDetailHabit._id, cell.iso)}
                                                    >
                                                        {cell.num}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* View 2: Full Year Mini-Months Grid */}
                                {detailYearView && (
                                    <div className="habitsy-year-view">
                                        <h3 className="habitsy-month-title" style={{ textAlign: 'center' }}>
                                            {calendarMonthDate.getFullYear()} Year in Review
                                        </h3>
                                        <div className="habitsy-year-grid">
                                            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((monthName, mIdx) => {
                                                const daysInMonth = new Date(calendarMonthDate.getFullYear(), mIdx + 1, 0).getDate();
                                                return (
                                                    <div key={monthName} className="habitsy-mini-month">
                                                        <div className="habitsy-mini-month-name">{monthName}</div>
                                                        <div className="habitsy-mini-dots">
                                                            {Array.from({ length: daysInMonth }).map((_, dIdx) => {
                                                                const d = new Date(calendarMonthDate.getFullYear(), mIdx, dIdx + 1);
                                                                const iso = toISODateLocal(d);
                                                                const isDone = isCompleted(activeDetailHabit._id, iso);
                                                                return (
                                                                    <span
                                                                        key={iso}
                                                                        className={`habitsy-mini-dot ${isDone ? 'is-done' : ''}`}
                                                                        style={isDone ? { backgroundColor: activeDetailHabit.color || '#7C75F8' } : {}}
                                                                    ></span>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="habitsy-detail-footer">
                                <button
                                    type="button"
                                    className="habitsy-btn-danger-outline"
                                    onClick={() => deleteHabit(activeDetailHabit._id)}
                                >
                                    Delete Habit
                                </button>
                                <button
                                    type="button"
                                    className="habitsy-btn-create"
                                    onClick={() => toggleCompletion(activeDetailHabit._id, today)}
                                >
                                    {isCompleted(activeDetailHabit._id, today) ? 'Mark Incomplete' : 'Complete Today'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create / Edit Habit Modal */}
                {showFormModal && (
                    <div className="habitsy-modal-backdrop" onClick={() => setShowFormModal(false)}>
                        <div className="habitsy-detail-modal" onClick={(e) => e.stopPropagation()}>
                            <h2 className="habitsy-brand-title" style={{ marginBottom: '18px' }}>
                                {editingHabit ? 'Edit Habit' : 'New Habit'}
                            </h2>

                            <form onSubmit={saveHabit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                <div className="habitsy-modal-scrollable-body">
                                    <div className="habitsy-form-group">
                                        <label>Habit Name</label>
                                        <input
                                            type="text"
                                            className="habitsy-input"
                                            placeholder="e.g. Read 20 pages, Workout..."
                                            value={formName}
                                            onChange={(e) => setFormName(e.target.value)}
                                            autoFocus
                                            required
                                        />
                                    </div>

                                    <div className="habitsy-form-group">
                                        <label>Goal / Frequency</label>
                                        <select
                                            className="habitsy-select"
                                            value={formGoal}
                                            onChange={(e) => setFormGoal(e.target.value)}
                                        >
                                            {GOAL_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </select>
                                        {formGoal === 'Custom' && (
                                            <input
                                                type="text"
                                                className="habitsy-input"
                                                style={{ marginTop: '8px' }}
                                                placeholder="Enter custom frequency (e.g. Monthly • 2/2)"
                                                value={customGoal}
                                                onChange={(e) => setCustomGoal(e.target.value)}
                                                required
                                            />
                                        )}
                                    </div>

                                    <div className="habitsy-form-group">
                                        <label>Choose Color</label>
                                        <div className="habitsy-color-row">
                                            <div className="habitsy-color-swatches">
                                                {PRESET_COLORS.map((c) => (
                                                    <button
                                                        key={c}
                                                        type="button"
                                                        className={`habitsy-color-dot ${formColor.toLowerCase() === c.toLowerCase() ? 'is-selected' : ''}`}
                                                        style={{ backgroundColor: c }}
                                                        onClick={() => setFormColor(c)}
                                                    ></button>
                                                ))}
                                            </div>
                                            <div className="habitsy-hex-input-wrap">
                                                <input
                                                    type="color"
                                                    className="habitsy-color-native-picker"
                                                    value={formColor.startsWith('#') && formColor.length === 7 ? formColor : '#7C75F8'}
                                                    onChange={(e) => setFormColor(e.target.value)}
                                                    title="Pick custom color"
                                                />
                                                <input
                                                    type="text"
                                                    className="habitsy-input habitsy-hex-text-input"
                                                    placeholder="#7C75F8"
                                                    value={formColor}
                                                    maxLength={7}
                                                    onChange={(e) => setFormColor(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Unified Clean Icon Picker with Subpopup Button */}
                                    <div className="habitsy-form-group">
                                        <label>Choose Icon</label>
                                        
                                        <div className="habitsy-icon-picker-clean-wrap">
                                            {/* Current Selected Icon Bar with Open Emoji Subpopup Button */}
                                            <div className="habitsy-icon-selection-bar">
                                                <div className="habitsy-current-icon-display">
                                                    <div className="habitsy-icon-preview-box" style={{ background: formColor || '#7C75F8' }}>
                                                        {renderHabitIcon(formIcon)}
                                                    </div>
                                                    <span className="habitsy-icon-label-text">
                                                        {isEmoji(formIcon) ? 'Selected Emoji' : 'Selected Icon'}
                                                    </span>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="habitsy-open-emoji-btn"
                                                    onClick={() => setShowEmojiModal(true)}
                                                >
                                                    <span>All Emojis</span>
                                                    <i className="fi fi-rr-smile"></i>
                                                </button>
                                            </div>

                                            {/* Standard Clean Icon Choices */}
                                            <div className="habitsy-quick-icons-grid">
                                                {PRESET_ICONS.map((item) => (
                                                    <button
                                                        key={item.icon}
                                                        type="button"
                                                        className={`habitsy-icon-choice ${formIcon === item.icon ? 'is-selected' : ''}`}
                                                        onClick={() => setFormIcon(item.icon)}
                                                        title={item.label}
                                                    >
                                                        <i className={`fi ${item.icon}`}></i>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="habitsy-form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input
                                            type="checkbox"
                                            id="formStarred"
                                            checked={formStarred}
                                            onChange={(e) => setFormStarred(e.target.checked)}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <label htmlFor="formStarred" style={{ margin: 0, cursor: 'pointer' }}>
                                            Set as Starred (Spotlight Card)
                                        </label>
                                    </div>
                                </div>

                                <div className="habitsy-form-actions">
                                    <button
                                        type="button"
                                        className="habitsy-btn-cancel"
                                        onClick={() => setShowFormModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="habitsy-btn-submit"
                                        disabled={!formName.trim()}
                                    >
                                        {editingHabit ? 'Save Changes' : 'Create Habit'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Sub-popup: Full Emoji Library Collection Modal */}
                {showEmojiModal && (
                    <div className="habitsy-emoji-modal-overlay" onClick={() => setShowEmojiModal(false)}>
                        <div className="habitsy-emoji-library-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="habitsy-emoji-modal-head">
                                <h3>Choose Emoji Icon</h3>
                                <button
                                    type="button"
                                    className="habitsy-nav-circle-btn"
                                    onClick={() => setShowEmojiModal(false)}
                                    aria-label="Close emoji library"
                                >
                                    <i className="fi fi-rr-cross-small"></i>
                                </button>
                            </div>

                            {/* Search Bar */}
                            <div className="habitsy-emoji-search-wrap">
                                <input
                                    type="text"
                                    className="habitsy-emoji-search-input"
                                    placeholder="Type to search or paste any emoji..."
                                    value={emojiSearch}
                                    onChange={(e) => setEmojiSearch(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {/* Category Filter Tabs */}
                            <div className="habitsy-emoji-categories-nav">
                                <button
                                    type="button"
                                    className={`habitsy-emoji-cat-tab ${selectedEmojiCategory === 'All' ? 'is-active' : ''}`}
                                    onClick={() => setSelectedEmojiCategory('All')}
                                >
                                    All
                                </button>
                                {EMOJI_CATEGORIES.map((cat) => (
                                    <button
                                        key={cat.name}
                                        type="button"
                                        className={`habitsy-emoji-cat-tab ${selectedEmojiCategory === cat.name ? 'is-active' : ''}`}
                                        onClick={() => setSelectedEmojiCategory(cat.name)}
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>

                            {/* Emoji Collection Grid */}
                            <div className="habitsy-emoji-library-scroll">
                                {emojiSearch.trim() && !filteredEmojiCategories.length && (
                                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                        <p style={{ color: 'var(--hb-text-secondary)', marginBottom: '12px' }}>
                                            Use custom input: "{emojiSearch}"
                                        </p>
                                        <button
                                            type="button"
                                            className="habitsy-btn-create"
                                            onClick={() => {
                                                setFormIcon(emojiSearch.trim());
                                                setShowEmojiModal(false);
                                            }}
                                        >
                                            Select "{emojiSearch.trim()}"
                                        </button>
                                    </div>
                                )}

                                {filteredEmojiCategories.map((cat) => (
                                    <div key={cat.name} className="habitsy-emoji-category-block">
                                        <div className="habitsy-emoji-category-title">{cat.name}</div>
                                        <div className="habitsy-emoji-grid-collection">
                                            {cat.emojis.map((emoji) => (
                                                <button
                                                    key={emoji}
                                                    type="button"
                                                    className={`habitsy-emoji-tile ${formIcon === emoji ? 'is-active' : ''}`}
                                                    onClick={() => {
                                                        setFormIcon(emoji);
                                                        setShowEmojiModal(false);
                                                    }}
                                                >
                                                    {emoji}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default HabitTracker;
